import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Globe, Link2, Trash2, UserPlus } from '@/components/icons';
import { api, errorMessage } from '@/lib/api';
import { copyText, formatDateTime } from '@/lib/format';
import type { Share } from '@/lib/types';
import { Button, Dialog, Field, Input, Select, Spinner, useConfirm, useToast } from '@/components/ui';
import { CopyField } from './CopyField';

function publicUrlOf(share: Share): string | null {
  if (share.publicUrl) return share.publicUrl;
  return share.publicToken ? `${window.location.origin}/shared/${share.publicToken}` : null;
}

export function ShareDialog({ runId, open, onClose }: { runId: string; open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const [email, setEmail] = useState('');
  const [expires, setExpires] = useState('30');
  const [latestUrl, setLatestUrl] = useState<string | null>(null);

  const shares = useQuery({ queryKey: ['shares', runId], queryFn: () => api.shares(runId), enabled: open });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['shares', runId] });

  const shareUser = useMutation({
    mutationFn: () => api.shareWithUser(runId, email.trim()),
    onSuccess: () => {
      toast.success(`Report shared with ${email.trim()}`);
      setEmail('');
      invalidate();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const sharePublic = useMutation({
    mutationFn: () => api.sharePublic(runId, expires === 'never' ? undefined : Number(expires)),
    onSuccess: (share) => {
      setLatestUrl(publicUrlOf(share));
      invalidate();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.deleteShare(id),
    onSuccess: () => {
      toast.success('Access removed');
      invalidate();
      qc.invalidateQueries({ queryKey: ['comments', runId] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const onShareUser = (e: FormEvent) => {
    e.preventDefault();
    if (email.trim()) shareUser.mutate();
  };

  const list = shares.data ?? [];

  return (
    <Dialog open={open} onClose={onClose} title="Share this report" description="Give a teammate read-only access, or create a link anyone can open." size="lg">
      <div className="space-y-6">
        <form onSubmit={onShareUser} className="space-y-2">
          <Field label="Share with a person" hint="They need a 1QA account (View Only users are perfect for this). They can send you comments about the report.">
            {(id) => (
              <div className="flex gap-2">
                <Input id={id} type="email" placeholder="teammate@company.com" value={email} onChange={(e) => setEmail(e.target.value)} />
                <Button type="submit" loading={shareUser.isPending} disabled={!email.trim()} icon={<UserPlus className="size-4" />}>
                  Share
                </Button>
              </div>
            )}
          </Field>
        </form>

        <div className="rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-200 dark:bg-slate-800/40 dark:ring-slate-700">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Globe className="size-4 text-brand-500" /> Public link
              </div>
              <p className="mt-0.5 text-xs text-slate-500">Anyone with the link can view this report - no sign-in needed.</p>
            </div>
            <Select
              aria-label="Link expiry"
              value={expires}
              onChange={(e) => setExpires(e.target.value)}
              wrapperClassName="w-36"
              options={[
                { value: '7', label: '7 days' },
                { value: '30', label: '30 days' },
                { value: '90', label: '90 days' },
                { value: 'never', label: 'Never expires' },
              ]}
            />
            <Button variant="outline" onClick={() => sharePublic.mutate()} loading={sharePublic.isPending} icon={<Link2 className="size-4" />}>
              Create link
            </Button>
          </div>
          {latestUrl && (
            <div className="mt-3">
              <CopyField value={latestUrl} label="public report link" />
            </div>
          )}
        </div>

        <div>
          <h3 className="mb-2 text-sm font-semibold">People and links with access</h3>
          {shares.isPending ? (
            <Spinner className="size-5 text-brand-500" />
          ) : list.length === 0 ? (
            <p className="text-sm text-slate-500">Only members of this organization can see this report.</p>
          ) : (
            <ul className="divide-y divide-slate-100 rounded-xl ring-1 ring-slate-200 dark:divide-slate-800 dark:ring-slate-700">
              {list.map((s) => {
                const url = publicUrlOf(s);
                return (
                  <li key={s.id} className="flex items-center gap-3 px-3.5 py-2.5 text-sm">
                    {url ? <Globe className="size-4 shrink-0 text-brand-500" /> : <UserPlus className="size-4 shrink-0 text-slate-400" />}
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium">{url ? 'Public link' : (s.user?.name ?? s.user?.email ?? 'User')}</div>
                      <div className="truncate text-xs text-slate-500">
                        {url ? (s.expiresAt ? `Expires ${formatDateTime(s.expiresAt)}` : 'Never expires') : s.user?.email}
                        {!url && s._count?.comments ? ` · ${s._count.comments} comment${s._count.comments === 1 ? '' : 's'}` : ''}
                      </div>
                    </div>
                    {url && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={async () => {
                          if (await copyText(url)) toast.success('Link copied');
                          else toast.error('Could not copy - select the link and copy it');
                        }}
                      >
                        Copy
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label="Remove access"
                      onClick={async () => {
                        const comments = s._count?.comments ?? 0;
                        if (comments > 0) {
                          const ok = await confirm({
                            title: 'Remove access?',
                            message: `${s.user?.name ?? 'This person'} can no longer open the report, and your ${comments} comment${comments === 1 ? '' : 's'} with them on it will be deleted.`,
                            confirmLabel: 'Remove access',
                          });
                          if (!ok) return;
                        }
                        revoke.mutate(s.id);
                      }}
                      disabled={revoke.isPending}
                      icon={<Trash2 className="size-4 text-rose-500" />}
                    />
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </Dialog>
  );
}
